import React from 'react';
import type {MenuProps} from 'antd';
import {
  AlipayOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  DashboardOutlined,
  FileImageOutlined,
  FileOutlined,
  FolderOutlined,
  GiftOutlined,
  HomeOutlined,
  InboxOutlined,
  PoundOutlined,
  RedEnvelopeOutlined,
  RetweetOutlined,
  SafetyCertificateOutlined,
  SecurityScanOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  TagsOutlined,
  TeamOutlined,
  TrademarkOutlined,
  TwitterOutlined,
  UserOutlined,
} from '@ant-design/icons';

export type MenuItem = (Required<MenuProps>['items'][number]) & {
  requiredPermission?: string | string[];
};

const menuItems: MenuItem[] = [
  {
    key: '/dashboard',
    icon: <DashboardOutlined/>,
    label: '仪表盘',
    requiredPermission: 'DASHBOARD_MANAGE',
    children: [
      {
        key: '/dashboard/operations',
        icon: <DashboardOutlined/>,
        label: '运营仪表盘',
        requiredPermission: 'DASHBOARD_MANAGE',
      },
      {
        key: '/dashboard/merchant-statistics',
        icon: <BarChartOutlined/>,
        label: '数据统计',
        requiredPermission: 'DASHBOARD_MANAGE',
      },
    ],
  },
  {
    key: '/user-center',
    icon: <UserOutlined/>,
    label: '用户中心',
    requiredPermission: ['SYS_USER_MANAGE', 'SYS_ROLE_MANAGE', 'PERMISSION_MANAGE'],
    children: [
      {
        key: '/user',
        icon: <UserOutlined/>,
        label: '用户管理',
        requiredPermission: 'SYS_USER_MANAGE',
      },
      {
        key: '/role',
        icon: <TeamOutlined/>,
        label: '角色管理',
        requiredPermission: 'SYS_ROLE_MANAGE',
      },
      {
        key: '/permission',
        icon: <SafetyCertificateOutlined/>,
        label: '权限管理',
        requiredPermission: 'PERMISSION_MANAGE',
      },
    ],
  },
  {
    key: '/merchant-center',
    icon: <ShopOutlined/>,
    label: '商户中心',
    requiredPermission: ['MERCHANT_MANAGE', 'ALIPAY_SUBJECT_MANAGE', 'MERCHANT_LEAD_MANAGE'],
    children: [
      {
        key: '/merchant',
        icon: <ShopOutlined/>,
        label: '商户管理',
        requiredPermission: 'MERCHANT_MANAGE',
      },
      {
        key: '/alipay-subject',
        icon: <AlipayOutlined/>,
        label: '支付宝主体',
        requiredPermission: 'ALIPAY_SUBJECT_MANAGE',
      },
      {
        key: '/merchant-lead',
        icon: <InboxOutlined/>,
        label: '商家线索',
        requiredPermission: 'MERCHANT_LEAD_MANAGE',
      },
    ],
  },
  {
    key: '/store',
    icon: <ShopOutlined/>,
    label: '门店管理',
    requiredPermission: 'STORE_MANAGE',
  },
  {
    key: '/page',
    icon: <FileOutlined/>,
    label: '页面管理',
    requiredPermission: 'HOME_MODULE_MANAGE',
    children: [
      {
        key: '/page/home',
        icon: <HomeOutlined/>,
        label: '租赁管理',
        requiredPermission: 'HOME_MODULE_MANAGE',
      },
      {
        key: '/page/recycle',
        icon: <RetweetOutlined/>,
        label: '回收管理',
        requiredPermission: 'HOME_MODULE_MANAGE',
      },
    ],
  },
  {
    key: '/product',
    icon: <ShoppingOutlined/>,
    label: '商品管理',
    requiredPermission: ['CATEGORY_MANAGE', 'BRAND_MANAGE', 'TAG_MANAGE', 'PRODUCT_MODEL_MANAGE', 'MODEL_ENROLLMENT_MANAGE', 'MODEL_ENROLLMENT_ADMIN_MANAGE', 'SPECIFICATION_MANAGE'],
    children: [
      {
        key: '/product/category',
        icon: <FolderOutlined/>,
        label: '商品分类',
        requiredPermission: 'CATEGORY_MANAGE',
      },
      {
        key: '/product/brand',
        icon: <TrademarkOutlined/>,
        label: '品牌管理',
        requiredPermission: 'BRAND_MANAGE',
      },
      {
        key: '/product/tag',
        icon: <TagsOutlined/>,
        label: '标签管理',
        requiredPermission: 'TAG_MANAGE',
      },
      {
        key: '/product/productModel',
        icon: <TagsOutlined/>,
        label: '型号管理',
        requiredPermission: 'PRODUCT_MODEL_MANAGE',
      },
      {
        key: '/product/model-enrollment',
        icon: <TagsOutlined/>,
        label: '型号报名',
        requiredPermission: 'MODEL_ENROLLMENT_MANAGE',
      },
      {
        key: '/product/model-enrollment-admin',
        icon: <TagsOutlined/>,
        label: '型号报名管理',
        requiredPermission: 'MODEL_ENROLLMENT_ADMIN_MANAGE',
      },
      {
        key: '/product/specification',
        icon: <TagsOutlined/>,
        label: '规格管理',
        requiredPermission: 'SPECIFICATION_MANAGE',
      },
    ],
  },
  {
    key: '/alipay-product',
    icon: <ShoppingOutlined/>,
    label: '支付宝商品管理',
    requiredPermission: ['PLATFORM_PRODUCT_MANAGE', 'MERCHANT_PRODUCT_MANAGE', 'LONG_RENT_PLATFORM_PRODUCT_MANAGE', 'LONG_RENT_MERCHANT_PRODUCT_MANAGE', 'OFFLINE_PRODUCT_MANAGE', 'PRODUCT_CREATE', 'PLATFORM_PRODUCT_CREATE', 'LONG_RENT_MERCHANT_PRODUCT_CREATE', 'LONG_RENT_PLATFORM_PRODUCT_CREATE'],
    children: [
      {
        key: '/product/platform/list',
        icon: <InboxOutlined/>,
        label: '短租公域商品',
        requiredPermission: 'PLATFORM_PRODUCT_MANAGE',
      },
      {
        key: '/product/merchant/list',
        icon: <InboxOutlined/>,
        label: '短租私域商品',
        requiredPermission: 'MERCHANT_PRODUCT_MANAGE',
      },
      {
        key: '/product/long-rent-platform/list',
        icon: <InboxOutlined/>,
        label: '长租公域商品',
        requiredPermission: 'LONG_RENT_PLATFORM_PRODUCT_MANAGE',
      },
      {
        key: '/product/long-rent-merchant/list',
        icon: <InboxOutlined/>,
        label: '长租私域商品',
        requiredPermission: 'LONG_RENT_MERCHANT_PRODUCT_MANAGE',
      },
      {
        key: '/product/offline/list',
        icon: <InboxOutlined/>,
        label: '线下商品',
        requiredPermission: 'OFFLINE_PRODUCT_MANAGE',
      },
      {
        key: '/product/image/upload',
        icon: <FileImageOutlined/>,
        label: '批量上传商品',
        requiredPermission: ['PRODUCT_CREATE', 'PLATFORM_PRODUCT_CREATE', 'LONG_RENT_MERCHANT_PRODUCT_CREATE', 'LONG_RENT_PLATFORM_PRODUCT_CREATE'],
      },
    ],
  },
  {
    key: '/jd-product',
    icon: <ShoppingOutlined/>,
    label: '京东商品管理',
    requiredPermission: 'JD_PRODUCT_MANAGE',
    children: [
      {
        key: '/product/jd/list',
        icon: <InboxOutlined/>,
        label: '京东公域商品',
        requiredPermission: 'JD_PRODUCT_MANAGE',
      },
      {
        key: '/product/jd/merchant/list',
        icon: <InboxOutlined/>,
        label: '京东私域商品',
        requiredPermission: 'JD_PRODUCT_MANAGE',
      },
    ],
  },
  {
    key: '/meituan-product',
    icon: <ShoppingOutlined/>,
    label: '美团商品管理',
    requiredPermission: ['MEITUAN_PRODUCT_MANAGE', 'MEITUAN_PRODUCT_CREATE'],
    children: [
      {
        key: '/meituan-product/list',
        icon: <InboxOutlined/>,
        label: '美团商品列表',
        requiredPermission: 'MEITUAN_PRODUCT_MANAGE',
      },
      {
        key: '/meituan-product/upload',
        icon: <FileImageOutlined/>,
        label: '美团上传商品',
        requiredPermission: 'MEITUAN_PRODUCT_CREATE',
      },
    ],
  },
  {
    key: '/order',
    icon: <ShoppingCartOutlined/>,
    label: '订单管理',
    requiredPermission: ['ORDER_MANAGE', 'LONG_RENT_ORDER_MANAGE', 'MEITUAN_ORDER_MANAGE', 'JD_ORDER_MANAGE', 'OFFLINE_ORDER_MANAGE', 'AFTER_SALE_MANAGE', 'COMPLAINT_MANAGE', 'SMS_RECORD_MANAGE'],
    children: [
      {
        key: '/order/list',
        label: '支付宝短租订单',
        requiredPermission: 'ORDER_MANAGE',
      },
      {
        key: '/order/long-rent/list',
        label: '支付宝长租订单',
        requiredPermission: 'LONG_RENT_ORDER_MANAGE',
      },
      {
        key: '/order/meituan',
        label: '美团订单',
        requiredPermission: 'MEITUAN_ORDER_MANAGE',
      },
      {
        key: '/order/jd',
        label: '京东订单',
        requiredPermission: 'JD_ORDER_MANAGE',
      },
      {
        key: '/order/offline',
        label: '线下订单',
        requiredPermission: 'OFFLINE_ORDER_MANAGE',
      },
      {
        key: '/order/buyout',
        label: '买断管理',
        requiredPermission: ['ORDER_MANAGE', 'LONG_RENT_ORDER_MANAGE'],
      },
      {
        key: '/order/aftersalelist',
        label: '售后列表',
        requiredPermission: 'AFTER_SALE_MANAGE',
      },
      {
        key: '/order/complaint',
        label: '投诉管理',
        requiredPermission: 'COMPLAINT_MANAGE',
      },
      {
        key: '/risk-control/sms-send-records',
        label: '短信发送记录',
        requiredPermission: 'SMS_RECORD_MANAGE',
      },
    ],
  },
  {
    key: '/risk-control',
    icon: <SecurityScanOutlined/>,
    label: '风控管理',
    requiredPermission: 'RISK_REPORT_MANAGE',
    children: [
      {
        key: '/risk-control/reports',
        label: '风控报告',
        requiredPermission: 'RISK_REPORT_MANAGE',
      },
    ],
  },
  {
    key: '/points',
    icon: <GiftOutlined/>,
    label: '积分管理',
    requiredPermission: 'POINTS_MANAGE',
    children: [
      {
        key: '/points/pointsProduct',
        label: '积分商品管理',
        requiredPermission: 'POINTS_MANAGE',
      },
      {
        key: '/points/ruleConfig',
        label: '积分规则管理',
        requiredPermission: 'POINTS_MANAGE',
      },
      {
        key: '/points/pointsOrder',
        label: '积分订单管理',
        requiredPermission: 'POINTS_MANAGE',
      },
    ],
  },
  {
    key: '/finance',
    icon: <AppstoreOutlined/>,
    label: '财务管理',
    requiredPermission: ['SETTLEMENT_MANAGE', 'OFFLINE_SETTLEMENT_MANAGE', 'REVENUE_MANAGE', 'DEPOSIT_MANAGE'],
    children: [
      {
        key: '/finance/settlement',
        icon: <TagsOutlined/>,
        label: '结算管理',
        requiredPermission: 'SETTLEMENT_MANAGE',
      },
      {
        key: '/finance/offline-settlement',
        icon: <TagsOutlined/>,
        label: '线下结算',
        requiredPermission: 'OFFLINE_SETTLEMENT_MANAGE',
      },
      {
        key: '/finance/revenue',
        icon: <TagsOutlined/>,
        label: '收益管理',
        requiredPermission: 'REVENUE_MANAGE',
      },
      {
        key: '/finance/deposit',
        icon: <TagsOutlined/>,
        label: '保证金管理',
        requiredPermission: 'DEPOSIT_MANAGE',
      },
    ],
  },
  {
    key: '/member',
    icon: <UserOutlined/>,
    label: '会员管理',
    requiredPermission: 'APP_USER_MANAGE',
  },
  {
    key: '/voucher',
    icon: <RedEnvelopeOutlined/>,
    label: '商家券管理',
    requiredPermission: ['VOUCHER_ACTIVITY_MANAGE', 'VOUCHER_MANAGE'],
    children: [
      {
        key: '/voucher/activity/list',
        icon: <RedEnvelopeOutlined/>,
        label: '商家券活动管理',
        requiredPermission: 'VOUCHER_ACTIVITY_MANAGE',
      },
      {
        key: '/voucher/marketing/image/list',
        icon: <FileImageOutlined/>,
        label: '商家券营销图片管理',
        requiredPermission: 'VOUCHER_MANAGE',
      },
    ],
  },

  {
    key: '/recycle',
    label: '回收管理',
    icon: <RetweetOutlined/>,
    requiredPermission: ['RECYCLE_CATEGORY_MANAGE', 'RECYCLE_ORDER_MANAGE', 'RECYCLE_VALUATION_MANAGE'],
    children: [
      {
        key: '/recycle/categories',
        icon: <FolderOutlined/>,
        label: '回收品类',
        requiredPermission: 'RECYCLE_CATEGORY_MANAGE',
      },
      {
        key: '/recycle/orders',
        icon: <ShoppingCartOutlined/>,
        label: '回收订单',
        requiredPermission: 'RECYCLE_ORDER_MANAGE',
      },
      {
        key: '/recycle/valuations',
        icon: <PoundOutlined/>,
        label: '估价配置',
        requiredPermission: 'RECYCLE_VALUATION_MANAGE',
      },
    ],
  },
  {
    key: '/tweet',
    icon: <TwitterOutlined/>,
    label: '推文管理',
    requiredPermission: 'TWEET_MANAGE',
    children: [
      {
        key: '/tweet/tweets',
        label: '推文管理',
        requiredPermission: 'TWEET_MANAGE',
      },
      {
        key: '/tweet/topic',
        label: '话题管理',
        requiredPermission: 'TWEET_MANAGE',
      },
      {
        key: '/tweet/category',
        label: '分类管理',
        requiredPermission: 'TWEET_MANAGE',
      },
      {
        key: '/tweet/comments',
        label: '评论管理',
        requiredPermission: 'TWEET_MANAGE',
      },
    ],
  },
  // {
  //   key: '/system',
  //   icon: <SettingOutlined />,
  //   label: '系统设置',
  // },
] as MenuItem[];

export default menuItems;
